import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import { AddonPricingModel } from '@lov/shared';
import { useAvailableAddons } from '../hooks/useAvailableAddons';
import { PeakPricingNotice } from './PeakPricingNotice';

interface AddonSelectorProps {
  venueInventoryId: string;
  date: string;
  startTime: string;
  endTime: string;
  selectedAddons: Map<string, number>;
  onAddonsChange: (addons: Map<string, number>) => void;
  enablePolling?: boolean;
  /** Peak pricing breakdown lines from the server (optional) */
  breakdown?: string[];
  /** True when the booking spans peak and non-peak windows (optional) */
  isProRated?: boolean;
}

export function AddonSelector({
  venueInventoryId,
  date,
  startTime,
  endTime,
  selectedAddons,
  onAddonsChange,
  enablePolling = false,
  breakdown = [],
  isProRated = false,
}: AddonSelectorProps) {
  const { addons, durationHours, isLoading } = useAvailableAddons(
    venueInventoryId,
    date,
    startTime,
    endTime,
    { enablePolling }
  );

  const handleToggle = (addonId: string) => {
    const newSelection = new Map(selectedAddons);
    if (newSelection.has(addonId)) {
      newSelection.delete(addonId);
    } else {
      newSelection.set(addonId, 1); // Default quantity: 1
    }
    onAddonsChange(newSelection);
  };

  const handleQuantityChange = (addonId: string, quantity: number) => {
    const addon = addons.find((a) => a.id === addonId);
    if (!addon) return;

    // Clamp quantity between 1 and maxQuantity
    const clampedQuantity = Math.max(1, Math.min(quantity, addon.maxQuantity));

    const newSelection = new Map(selectedAddons);
    newSelection.set(addonId, clampedQuantity);
    onAddonsChange(newSelection);
  };

  const formatPrice = (pricePerUnit: number, pricingModel: AddonPricingModel): string => {
    const formatted = `Rs. ${pricePerUnit.toFixed(2)}`;
    switch (pricingModel) {
      case AddonPricingModel.HOURLY:
        return `${formatted}/hour`;
      case AddonPricingModel.HALF_HOURLY:
        return `${formatted}/30 min`;
      case AddonPricingModel.FIXED:
        return formatted;
      default:
        return formatted;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (addons.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        No add-ons available for this item
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Add-On Equipment
      </Typography>

      {durationHours > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Duration: {durationHours} {durationHours === 1 ? 'hour' : 'hours'}
        </Typography>
      )}

      <PeakPricingNotice breakdown={breakdown} isProRated={isProRated} />

      {addons.map((addon) => (
        <Paper key={addon.id} sx={{ p: 2 }} elevation={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedAddons.has(addon.id)}
                  onChange={() => handleToggle(addon.id)}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {addon.name}
                    {addon.isRequired && (
                      <Chip
                        size="small"
                        label="Required"
                        color="primary"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Typography>
                  {addon.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {addon.description}
                    </Typography>
                  )}
                  <Typography variant="body2" color="primary" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {formatPrice(addon.pricePerUnit, addon.pricingModel)}
                  </Typography>
                  {addon.recommendationText && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      💡 {addon.recommendationText}
                    </Typography>
                  )}
                </Box>
              }
            />

            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                size="small"
                label={`${addon.availableQuantity} available`}
                color={addon.availableQuantity > 3 ? 'success' : 'warning'}
                sx={{ minWidth: 100 }}
              />

              {selectedAddons.has(addon.id) && (
                <TextField
                  type="number"
                  size="small"
                  label="Qty"
                  inputProps={{
                    min: 1,
                    max: addon.maxQuantity,
                  }}
                  value={selectedAddons.get(addon.id) || 1}
                  onChange={(e) => handleQuantityChange(addon.id, parseInt(e.target.value) || 1)}
                  sx={{ width: 80 }}
                />
              )}
            </Box>
          </Box>
        </Paper>
      ))}
    </Box>
  );
}
